import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  Chip,
  Rating,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  LocationOn as LocationIcon,
  Euro as EuroIcon,
  Visibility as VisibilityIcon,
  Message as MessageIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { toggleFavorite } from '../redux/slices/professionalsSlice';
import { useNavigate } from 'react-router-dom';
import './ProfessionalCard.css';

const ProfessionalCard = ({ professional, variant = 'grid' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    dispatch(toggleFavorite(professional.id));
  };

  const handleViewProfile = (e) => {
    e.stopPropagation();
    navigate(`/professional/${professional.id}`);
  };

  const handleContact = (e) => {
    e.stopPropagation();
    // Simuler l'ouverture d'une modal de contact
    console.log('Contacter:', `${professional.firstName} ${professional.lastName.charAt(0)}.`);
  };

  const handleCardClick = () => {
    navigate(`/professional/${professional.id}`);
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'immediate':
        return 'success';
      case 'week':
        return 'warning';
      case 'month':
        return 'info';
      default:
        return 'default';
    }
  };

  const getAvailabilityText = (availability) => {
    switch (availability) {
      case 'immediate':
        return 'Disponible maintenant';
      case 'week':
        return 'Cette semaine';
      case 'month':
        return 'Ce mois';
      case 'flexible':
        return 'Horaires flexibles';
      default:
        return 'Sur demande';
    }
  };

  if (variant === 'list') {
    return (
      <Card
        className="professional-card professional-card--list"
        onClick={handleCardClick}
        sx={{
          display: 'flex',
          mb: 2,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          },
          borderRadius: '15px',
          overflow: 'hidden',
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: isMobile ? 100 : 150,
            height: isMobile ? 100 : 150,
            objectFit: 'cover',
          }}
          image={professional.profileImage}
          alt={`${professional.firstName} ${professional.lastName.charAt(0)}.`}
        />
        
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <CardContent sx={{ flex: 1, p: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {professional.firstName} {professional.lastName.charAt(0)}.
                  </Typography>
                  {professional.isVerified && (
                    <Tooltip title="Profil vérifié">
                      <VerifiedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    </Tooltip>
                  )}
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {professional.discipline}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {professional.location}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Rating value={professional.rating} readOnly size="small" precision={0.1} />
                    <Typography variant="body2" color="text.secondary">
                      ({professional.reviewsCount})
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EuroIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {professional.hourlyRate}{professional.currency}/h
                    </Typography>
                  </Box>
                </Box>
                
                <Chip
                  label={getAvailabilityText(professional.availability)}
                  color={getAvailabilityColor(professional.availability)}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Box>
              
              <IconButton
                onClick={handleToggleFavorite}
                sx={{ color: professional.isFavorite ? 'error.main' : 'text.secondary' }}
              >
                {professional.isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewProfile}
                startIcon={<VisibilityIcon />}
                size="small"
                sx={{ textTransform: 'none', borderRadius: '20px' }}
              >
                Voir profil
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleContact}
                startIcon={<MessageIcon />}
                size="small"
                sx={{ textTransform: 'none', borderRadius: '20px' }}
              >
                Contacter
              </Button>
            </Box>
          </CardContent>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      className="professional-card professional-card--grid"
      onClick={handleCardClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
        },
        borderRadius: '20px',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Favorite Button */}
      <IconButton
        onClick={handleToggleFavorite}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: professional.isFavorite ? 'error.main' : 'text.secondary',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            transform: 'scale(1.1)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        {professional.isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
      </IconButton>

      {/* Professional Image */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          height="200"
          image={professional.profileImage}
          alt={`${professional.firstName} ${professional.lastName.charAt(0)}.`}
          sx={{
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
        />
        
        {/* Availability Badge */}
        <Chip
          label={getAvailabilityText(professional.availability)}
          color={getAvailabilityColor(professional.availability)}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            fontWeight: 600,
          }}
        />
        
        {/* Verified Badge */}
        {professional.isVerified && (
          <Tooltip title="Profil vérifié">
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VerifiedIcon sx={{ color: 'white', fontSize: 16 }} />
            </Box>
          </Tooltip>
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Professional Name */}
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {professional.firstName} {professional.lastName.charAt(0)}.
        </Typography>

        {/* Discipline */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {professional.discipline}
        </Typography>

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {professional.location}
          </Typography>
        </Box>

        {/* Rating and Reviews */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Rating value={professional.rating} readOnly size="small" precision={0.1} />
          <Typography variant="body2" color="text.secondary">
            ({professional.reviewsCount})
          </Typography>
        </Box>

        {/* Price */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <EuroIcon sx={{ fontSize: 18, color: 'success.main' }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'success.main',
            }}
          >
            {professional.hourlyRate}{professional.currency}/h
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleViewProfile}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: '25px',
              fontWeight: 600,
              py: 1,
            }}
          >
            Voir profil
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleContact}
            sx={{
              minWidth: 'auto',
              borderRadius: '25px',
              p: 1,
            }}
          >
            <MessageIcon />
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfessionalCard;
